import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, withLatestFrom } from 'rxjs';
import {
  AskKpiQuestion,
  CreateSession,
  CreateSessionAndPostQuestion,
  CreateSessionAndPostQuestionFailed,
  CreateSessionAndPostQuestionSuccess,
  CreateSessionFailed,
  CreateSessionSuccess,
  LoadSessionHistory,
  LoadSessionHistoryFailed,
  LoadSessionHistorySuccess,
  LoadSessions,
  LoadSessionsFailed,
  LoadSessionsSuccess,
  LoadUsage,
  LoadUsageFailed,
  LoadUsageSuccess,
  OpenSession,
  PostQuestion,
  PostQuestionFailed,
  PostQuestionSuccess,
} from '../actions/chatbot.actions';
import { kpiThreadId, sessionThreadId } from '../models/chatbot.model';
import { AlioState } from '../reducers';
import { getConversations } from '../selectors/chatbot.selectors';
import { ChatbotService } from '../services/chatbot.service';

@Injectable()
export class ChatbotEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AlioState>,
    private chatbotService: ChatbotService,
  ) {}

  loadSessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadSessions),
      switchMap(() =>
        this.chatbotService.getSessions().pipe(
          map((sessions) => LoadSessionsSuccess({ sessions })),
          catchError((error) => of(LoadSessionsFailed({ error }))),
        ),
      ),
    ),
  );

  loadUsage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadUsage),
      switchMap(() =>
        this.chatbotService.getUsage().pipe(
          map((usage) => LoadUsageSuccess({ usage })),
          catchError((error) => of(LoadUsageFailed({ error }))),
        ),
      ),
    ),
  );

  refreshUsageAfterAnswer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PostQuestionSuccess),
      filter(({ res }) => res.includes('<END>')),
      map(() => LoadUsage()),
    ),
  );

  openSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpenSession),
      withLatestFrom(this.store.select(getConversations)),
      filter(([{ sessionId }, conversations]) => {
        const conversation = conversations[sessionThreadId(sessionId)];
        return !conversation || conversation.messages.length === 0;
      }),
      map(([{ sessionId }]) => LoadSessionHistory({ sessionId })),
    ),
  );

  loadSessionHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadSessionHistory),
      switchMap(({ sessionId }) =>
        this.chatbotService.getHistory(sessionId).pipe(
          map((history) => LoadSessionHistorySuccess({ sessionId, history })),
          catchError((error) => of(LoadSessionHistoryFailed({ sessionId, error }))),
        ),
      ),
    ),
  );

  askKpiQuestion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AskKpiQuestion),
      withLatestFrom(this.store.select(getConversations)),
      map(([{ kpiId }, conversations]) => ({ threadId: kpiThreadId(kpiId), conversations })),
      filter(({ threadId, conversations }) => !conversations[threadId]?.sessionId),
      switchMap(({ threadId }) =>
        this.chatbotService.createSession().pipe(
          map((sessionId) => CreateSessionAndPostQuestionSuccess({ threadId, sessionId })),
          catchError((error) => of(CreateSessionAndPostQuestionFailed({ threadId, error }))),
        ),
      ),
    ),
  );

  createSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateSession),
      switchMap(({ threadId }) =>
        this.chatbotService.createSession().pipe(
          map((sessionId) => CreateSessionSuccess({ threadId, sessionId })),
          catchError((error) => of(CreateSessionFailed({ threadId, error }))),
        ),
      ),
    ),
  );

  postQuestion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PostQuestion, CreateSessionAndPostQuestionSuccess),
      switchMap(({ threadId }) =>
        this.chatbotService.postQuestion(threadId).pipe(
          map((chunk) => PostQuestionSuccess({ threadId, res: chunk })),
          catchError((error) => of(PostQuestionFailed({ threadId, error }))),
        ),
      ),
    ),
  );

  createSessionAndPostQuestion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateSessionAndPostQuestion),
      switchMap(({ threadId }) =>
        this.chatbotService.createSession().pipe(
          map((sessionId) => CreateSessionAndPostQuestionSuccess({ threadId, sessionId })),
          catchError((error) => of(CreateSessionAndPostQuestionFailed({ threadId, error }))),
        ),
      ),
    ),
  );
}
